import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    IonButton,
    IonContent,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    RefresherEventDetail,
    ScrollDetail,
    useIonRouter,
    useIonViewDidEnter,
} from "@ionic/react";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { RootState } from "../../redux/store";
import { HeaderComponent } from "../../components";
import { routes } from "../../route/constants";
import { useVenueBoard } from "../../components/venue/useVenueBoard";
import {
    METRIC_SECTIONS,
    metricLabel,
    rankingForMetric,
    recordForMetric,
    historyBest,
    matchMetricValue,
    VenueMetric,
    VENUE_METRICS,
} from "./leaderboardModel";
import { computeVenueHistory } from "./venueHistory";
import { VenueHero } from "./components/VenueHero";
import { PersonalBestTiles } from "./components/PersonalBestTiles";
import { RecordHolderCard } from "./components/RecordHolderCard";
import { LegendBoard } from "./components/LegendBoard";
import { YourHistoryList } from "./components/YourHistoryList";
import { SegmentToggle } from "./components/SegmentToggle";
import "./venuePage.css";
import {
    getLeaderboardPreferences,
    isEligible,
} from "../../services/leaderboardOptInService";
import {
    forgetVenueScroll,
    rememberVenueScroll,
    venueScrollFor,
} from "./venueScroll";

/**
 * The venue leaderboards page (SCORZA-739).
 *
 * Two halves, deliberately interleaved rather than stacked:
 *  1. **Yours** — personal bests and your best games here, computed locally from the matches
 *     already in redux. This is what makes the page worth opening at density = 1, which is
 *     where most venues will sit for a long while.
 *  2. **The community board** — venue records over two windows and the Local Legend standings.
 *     Additive: with no snapshot yet we say so plainly rather than inventing a leaderboard.
 *
 * Reached either as `/venue/:venueId` (deep link) or `/venue?lat=&lon=&venueName=` from a match
 * result, which knows where the match was played but not which venue that is. A `section` query
 * param scrolls to one section, which is how the result-page ribbons deep-link in.
 */

const KEY = "leaderboards.VenuePage.";

/**
 * How long to keep trying to put the page back where the user left it, and how many frames of an
 * unchanged page height to accept as "this is as tall as it is going to get". The page grows for
 * a while after the board renders — the hero's map, the fonts — so a single frame's failure to
 * reach the old depth means nothing.
 */
const RESTORE_BUDGET_MS = 5000;
const RESTORE_STABLE_FRAMES = 20;

/**
 * Scroll back to where the user was before they tapped through to a match.
 *
 * One assignment is not enough: coming back, the outlet rebuilds this page rather than showing
 * the one it kept, so for the first frames it is shorter than it was and the browser clamps any
 * scroll past the current height. So retry across frames until it lands, until the page stops
 * growing short of it, or until the user scrolls themselves — which is the one thing that must
 * cancel this outright, since fighting the reader for the scroll position is worse than losing
 * their place.
 */
function restoreScroll(
    content: HTMLIonContentElement,
    top: number,
    onDone: () => void
): void {
    const deadline = Date.now() + RESTORE_BUDGET_MS;
    let written = -1;
    let height = -1;
    let unchangedFrames = 0;

    const attempt = () => {
        // `getScrollElement` is missing wherever Ionic's custom elements have not been defined
        // (jsdom, most notably), and a scroll nobody can see is not worth throwing over.
        let scroller: Promise<HTMLElement>;
        try {
            scroller = content.getScrollElement();
        } catch {
            onDone();
            return;
        }

        scroller
            .then((element) => {
                if (written >= 0 && element.scrollTop !== written) {
                    // Something else moved the page: the user is reading it now, not us.
                    onDone();
                    return;
                }

                element.scrollTop = top;
                written = element.scrollTop;
                if (written === top) {
                    onDone();
                    return;
                }

                unchangedFrames =
                    element.scrollHeight === height ? unchangedFrames + 1 : 0;
                height = element.scrollHeight;
                if (
                    unchangedFrames >= RESTORE_STABLE_FRAMES ||
                    Date.now() > deadline
                ) {
                    onDone();
                    return;
                }

                requestAnimationFrame(attempt);
            })
            .catch(onDone);
    };

    requestAnimationFrame(attempt);
}

type RecordWindow = "month" | "all";

/** `decodeURIComponent` throws on a stray `%`; a malformed id should 400, not crash the page. */
function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

function useQuery(): URLSearchParams {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

const VenuePage: FC = () => {
    const { t } = useTranslation();
    const router = useIonRouter();
    const query = useQuery();
    const location = useLocation();
    const content = useRef<HTMLIonContentElement>(null);
    const scrollTop = useRef(0);
    // Keyed by the whole URL: a different venue, or the same venue opened from a different
    // match, is a different page and opens at the top like any other.
    const scrollKey = `${location.pathname}${location.search}`;
    const { venueId: rawVenueId } = useParams<{ venueId?: string }>();
    // react-router v5 hands back the raw path segment, so `/venue/venue%3Aabc12345` arrives
    // still percent-encoded and `getVenue` would encode it a second time — the backend's
    // `^venue:[A-Za-z0-9_-]{1,32}$` check then rejects it as a malformed id. Decoding an id
    // that was never encoded is a no-op, so this is safe for both link shapes.
    const venueIdParam = rawVenueId ? safeDecode(rawVenueId) : rawVenueId;

    const allMatches = useSelector((state: RootState) => state.matches.matches);
    const profile = useSelector(
        (state: RootState) => state.currentUser?.profile
    );
    const onBoards = isEligible(profile);
    const myDisplayName = getLeaderboardPreferences(profile).displayName;

    const lat = Number(query.get("lat"));
    const lon = Number(query.get("lon"));
    const venueNameParam = query.get("venueName");
    const section = query.get("section");
    const matchIdParam = query.get("matchId");

    const centre = query.has("lat")
        ? { latitude: lat, longitude: lon }
        : undefined;
    const { venue, board, community, reload } = useVenueBoard({
        venueId: venueIdParam,
        centre,
    });

    // All time first: it is the hall of fame, always populated where the rolling board may not
    // be, so it is the one that has something to show when the page opens.
    const [recordWindow, setRecordWindow] = useState<RecordWindow>("all");
    const [historyMetric, setHistoryMetric] = useState<VenueMetric>("speedMax");

    // The venue name the user typed on their own matches is what keys their local history; the
    // backend's canonical name is the fallback for a deep link that never came from a match.
    const localVenueName = venueNameParam || venue?.name || null;
    const history = useMemo(
        () =>
            computeVenueHistory(allMatches, localVenueName, board?.windowDays),
        [allMatches, localVenueName, board?.windowDays]
    );

    // The result the user tapped a ribbon on. The boards themselves rank personal bests, so
    // this is the one number they cannot read off the page otherwise — "how did today go
    // against this ground's record?". Absent on a deep link that came from no match.
    const thisMatch = useMemo(
        () =>
            matchIdParam
                ? (allMatches || []).find(
                      (candidate) => candidate._id === matchIdParam
                  )
                : undefined,
        [allMatches, matchIdParam]
    );

    // A metric section deep-link switches the history toggle to that metric, so the page opens
    // showing the thing the ribbon was about. The page itself stays scrolled to the top.
    useEffect(() => {
        if (!section) {
            return;
        }
        const metric = VENUE_METRICS.find(
            (candidate) => METRIC_SECTIONS[candidate] === section
        );
        if (metric) {
            setHistoryMetric(metric);
        }
    }, [section]);

    // Any number on this page that belongs to the user opens the match that set it, pushed
    // forward so the back button returns here rather than to the results tab — and to the depth
    // they were reading at, which is most of the way down a page this long.
    const openMatch = useCallback(
        (matchId: string) => {
            rememberVenueScroll(scrollKey, scrollTop.current);
            router.push(`${routes.match}/${matchId}/overview`, "forward");
        },
        [router, scrollKey]
    );

    const restoring = useRef(false);
    const restore = useCallback(() => {
        // Not while the community half is still resolving: the page is barely a screen tall
        // until the boards render, so a restore now would clamp to that and then give up.
        if (community === "loading" || restoring.current || !content.current) {
            return;
        }
        const top = venueScrollFor(scrollKey);
        if (!top) {
            return;
        }
        restoring.current = true;
        restoreScroll(content.current, top, () => {
            restoring.current = false;
            forgetVenueScroll();
        });
    }, [community, scrollKey]);

    // Both paths back, because which one happens is Ionic's business: the outlet rebuilds this
    // page (what it does today, so the effect is what runs), or it kept the page mounted and
    // simply shows it again, which fires the lifecycle event and no effect at all.
    useIonViewDidEnter(restore);
    useEffect(restore, [restore]);

    const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
        await reload();
        event.detail.complete();
    };

    const attribution =
        venue?.attribution ||
        (venue?.nameSource === "osm" ? t(KEY + "attributionOsm") : null);

    const activeRecords =
        recordWindow === "all"
            ? board?.records?.allTime
            : board?.records?.rollingMonth;
    const activeRankings =
        recordWindow === "all"
            ? board?.rankings?.allTime
            : board?.rankings?.rollingMonth;
    const windowLabel = t(
        KEY + (recordWindow === "all" ? "recordsAllTime" : "recordsThisMonth")
    );

    return (
        <IonPage>
            <HeaderComponent
                title={t(KEY + "title")}
                titleTestId="venue-title"
                showBackButton
                defaultHref={routes.tabResults}
            />
            <IonContent
                className="venue-page-content"
                ref={content}
                scrollEvents
                onIonScroll={(event: CustomEvent<ScrollDetail>) => {
                    scrollTop.current = event.detail.scrollTop;
                }}
            >
                {/* The load-error copy tells the user to pull down, so make that work. */}
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent />
                </IonRefresher>

                <VenueHero
                    venue={venue}
                    fallbackName={venueNameParam}
                    apps={history.matchCount}
                    playerCount={venue?.playerCount}
                    entryCount={venue?.entryCount}
                />

                {/* Personal half first: it works solo, which is the common case for now. */}
                <div className="venue-section" data-testid="venue-your-history">
                    <div className="venue-section-head">
                        <span
                            className="venue-section-head-icon"
                            aria-hidden="true"
                        >
                            📊
                        </span>
                        <h2>{t(KEY + "personalBests")}</h2>
                    </div>
                    <p className="venue-section-note">
                        {t(KEY + "personalBestsExplainer")}
                    </p>
                    <PersonalBestTiles
                        history={history}
                        allTime={board?.records?.allTime}
                        onOpenMatch={openMatch}
                    />
                </div>

                {community === "loading" && (
                    <div className="venue-section">
                        <div className="venue-loading">
                            <IonSpinner data-testid="venue-board-loading" />
                            <span className="text-small">
                                {t(KEY + "resolving")}
                            </span>
                        </div>
                    </div>
                )}

                {community === "error" && (
                    <div className="venue-section">
                        <div className="venue-state-card">
                            <span
                                className="text-small"
                                data-testid="venue-load-error"
                            >
                                {t(KEY + "loadError")}
                            </span>
                        </div>
                    </div>
                )}

                {community === "notMapped" && (
                    <div className="venue-section">
                        <div className="venue-state-card">
                            <div className="venue-card-title">
                                {t(KEY + "noBoardYet")}
                            </div>
                            <span
                                className="text-small"
                                data-testid="venue-not-mapped"
                            >
                                {t(KEY + "notFound")}
                            </span>
                        </div>
                    </div>
                )}

                {community === "ready" && !board && (
                    <div className="venue-section">
                        <div className="venue-state-card">
                            <div className="venue-card-title">
                                {t(KEY + "noBoardYet")}
                            </div>
                            <span
                                className="text-small"
                                data-testid="venue-no-board"
                            >
                                {t(KEY + "noBoardYetExplainer")}
                            </span>
                        </div>
                    </div>
                )}

                {community === "ready" && board && (
                    <>
                        <div className="venue-section">
                            <div className="venue-section-head">
                                <span
                                    className="venue-section-head-icon"
                                    aria-hidden="true"
                                >
                                    🏆
                                </span>
                                <h2>{t(KEY + "venueRecords")}</h2>
                            </div>
                            <p className="venue-section-note">
                                {t(KEY + "venueRecordsExplainer")}
                            </p>

                            <SegmentToggle<RecordWindow>
                                label={t(KEY + "venueRecords")}
                                testId="venue-window-toggle"
                                gold={recordWindow === "all"}
                                value={recordWindow}
                                onChange={setRecordWindow}
                                options={[
                                    {
                                        value: "month",
                                        label: t(KEY + "recordsThisMonth"),
                                    },
                                    {
                                        value: "all",
                                        label: t(KEY + "recordsAllTime"),
                                    },
                                ]}
                            />

                            {/* The testid tracks the active window so the existing page objects
                                and specs keep addressing the set they mean. */}
                            <div
                                className="venue-record-cards"
                                data-testid={
                                    recordWindow === "all"
                                        ? "venue-records-all-time"
                                        : "venue-records-month"
                                }
                            >
                                {VENUE_METRICS.map((metric) => (
                                    <RecordHolderCard
                                        key={metric}
                                        metric={metric}
                                        record={recordForMetric(
                                            activeRecords,
                                            metric
                                        )}
                                        ranking={rankingForMetric(
                                            activeRankings,
                                            metric
                                        )}
                                        yourBest={historyBest(history, metric)}
                                        thisMatch={matchMetricValue(
                                            thisMatch,
                                            metric
                                        )}
                                        hallOfFame={recordWindow === "all"}
                                        windowLabel={windowLabel}
                                        myDisplayName={myDisplayName}
                                        onBoards={onBoards}
                                        myMatches={history.matches}
                                        onOpenMatch={openMatch}
                                        testId={`${
                                            recordWindow === "all"
                                                ? "venue-records-all-time"
                                                : "venue-records-month"
                                        }-${METRIC_SECTIONS[metric]}`}
                                    />
                                ))}
                            </div>
                        </div>

                        <div
                            className="venue-section"
                            data-testid="venue-local-legend"
                        >
                            <div className="venue-section-head">
                                <span
                                    className="venue-section-head-icon"
                                    aria-hidden="true"
                                >
                                    👑
                                </span>
                                <h2>{t(KEY + "localLegend")}</h2>
                            </div>
                            <p className="venue-section-note">
                                {t(KEY + "localLegendExplainerLong", {
                                    count: board.windowDays,
                                })}
                            </p>

                            {board.localLegend ? (
                                <LegendBoard
                                    board={board}
                                    yourWindowApps={history.windowMatchCount}
                                    myDisplayName={myDisplayName}
                                    onBoards={onBoards}
                                />
                            ) : (
                                <div className="venue-state-card">
                                    <span
                                        className="text-small"
                                        data-testid="venue-legend-empty"
                                    >
                                        {t(KEY + "noBoardYetExplainer")}
                                    </span>
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="venue-section">
                    <div className="venue-section-head">
                        <span
                            className="venue-section-head-icon"
                            aria-hidden="true"
                        >
                            📈
                        </span>
                        <h2>{t(KEY + "yourHistory")}</h2>
                    </div>
                    <p className="venue-section-note">
                        {t(KEY + "yourHistoryExplainer")}
                    </p>
                    <SegmentToggle<VenueMetric>
                        label={t(KEY + "yourHistory")}
                        testId="venue-history-toggle"
                        value={historyMetric}
                        onChange={setHistoryMetric}
                        options={VENUE_METRICS.map((metric) => ({
                            value: metric,
                            label: metricLabel(metric, t),
                        }))}
                    />
                    <YourHistoryList
                        matches={history.matches}
                        metric={historyMetric}
                        currentMatchId={thisMatch?._id}
                        onOpenMatch={openMatch}
                    />
                </div>

                {/* Boards are on by default, so this only shows for someone who has left
                    or is under 18 — but never imply they are on a board when they are not. */}
                {!onBoards && (
                    <div className="venue-section" data-testid="venue-opt-in">
                        <div className="venue-state-card">
                            <p className="venue-section-note">
                                {t(KEY + "optInPrompt")}
                            </p>
                            <IonButton
                                expand="block"
                                className="width-100"
                                data-testid="venue-opt-in-button"
                                onClick={() => router.push(routes.moreSettings)}
                            >
                                {t(KEY + "optInCta")}
                            </IonButton>
                        </div>
                    </div>
                )}

                <div className="venue-footer-meta">
                    {board && (
                        <div data-testid="venue-generated-at">
                            {t(KEY + "generatedAt", {
                                date: moment(board.generatedAt).format("lll"),
                            })}
                        </div>
                    )}
                    {attribution && (
                        <div data-testid="venue-attribution">{attribution}</div>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default VenuePage;
